import { useEffect, useRef, useState, useCallback } from 'react';
import { useLogin } from '@refinedev/core';
import { Button, Form, Input, Layout, Typography, theme } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';

const { Content } = Layout;
const { Text } = Typography;

/** Turnstile Site Key — 沒設就跳過驗證碼 */
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

/**
 * Turnstile widget 容器
 */
const TurnstileWidget: React.FC<{
  onVerify: (token: string) => void;
  onExpire: () => void;
}> = ({ onVerify, onExpire }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const { token: themeToken } = theme.useToken();
  const isDark = themeToken.colorBgLayout !== '#ffffff';

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    const renderWidget = () => {
      if (!containerRef.current || widgetIdRef.current !== null) return;
      const win = window as unknown as {
        turnstile?: {
          render: (
            el: HTMLElement,
            opts: Record<string, unknown>,
          ) => string;
          remove: (id: string) => void;
        };
      };
      if (!win.turnstile) return;

      widgetIdRef.current = win.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: isDark ? 'dark' : 'light',
        callback: onVerify,
        'expired-callback': onExpire,
        'error-callback': onExpire,
      });
    };

    // 動態載入 Turnstile script
    const existingScript = document.querySelector(
      'script[src*="challenges.cloudflare.com/turnstile"]',
    );
    if (existingScript) {
      renderWidget();
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.onload = () => renderWidget();
      document.head.appendChild(script);
    }

    return () => {
      const win = window as unknown as {
        turnstile?: { remove: (id: string) => void };
      };
      if (widgetIdRef.current && win.turnstile) {
        win.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onVerify, onExpire, isDark]);

  if (!TURNSTILE_SITE_KEY) return null;

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: 16,
      }}
    />
  );
};

/**
 * 登入頁面 — VPN 管理後台
 * 路由：/manage
 */
export const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const { mutate: login, isPending } = useLogin();
  const { token: themeToken } = theme.useToken();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleSubmit = (values: { username: string; password: string }) => {
    // 如果有設定 Turnstile Site Key，必須先通過驗證
    if (TURNSTILE_SITE_KEY && !turnstileToken) return;
    login({ ...values, turnstileToken });
  };

  const isTurnstileRequired = !!TURNSTILE_SITE_KEY;
  const isSubmitDisabled = isTurnstileRequired && !turnstileToken;

  return (
    <Layout
      style={{
        minHeight: '100vh',
        background: themeToken.colorBgLayout,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Content
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 24,
          width: '100%',
        }}
      >
        <div
          style={{
            width: 400,
            maxWidth: '100%',
            background: themeToken.colorBgContainer,
            borderRadius: themeToken.borderRadiusLG,
            borderLeft: `4px solid ${themeToken.colorPrimary}`,
            boxShadow: themeToken.boxShadowTertiary,
            padding: '48px 40px 40px',
          boxSizing: 'border-box' as const,
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: 40 }}>
            <h2
              data-testid="login-title"
              style={{
                fontSize: 28,
                fontWeight: 600,
                color: themeToken.colorText,
                margin: 0,
                lineHeight: 1.3,
                letterSpacing: '-0.01em',
              }}
            >
              System Login
            </h2>
            <Text
              style={{
                fontSize: 15,
                color: themeToken.colorTextSecondary,
                marginTop: 4,
                display: 'block',
              }}
            >
              Management Console
            </Text>
          </div>

          {/* Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            requiredMark={false}
          >
            <Form.Item
              name="username"
              rules={[
                { required: true, message: '請輸入帳號' },
                { min: 4, message: '帳號至少 4 個字元' },
              ]}
            >
              <Input
                id="username"
                prefix={<UserOutlined style={{ color: themeToken.colorTextQuaternary }} />}
                placeholder="帳號"
                size="large"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: '請輸入密碼' },
                { min: 5, message: '密碼至少 5 個字元' },
              ]}
            >
              <Input.Password
                id="password"
                prefix={<LockOutlined style={{ color: themeToken.colorTextQuaternary }} />}
                placeholder="密碼"
                size="large"
                autoComplete="current-password"
              />
            </Form.Item>

            {/* Cloudflare Turnstile */}
            <TurnstileWidget
              onVerify={handleTurnstileVerify}
              onExpire={handleTurnstileExpire}
            />

            <Form.Item style={{ marginBottom: 0, marginTop: 12 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={isPending}
                disabled={isSubmitDisabled}
                block
                size="large"
                data-testid="login-submit-btn"
              >
                登入
              </Button>
            </Form.Item>
          </Form>

          {/* Footer */}
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <Text style={{ fontSize: 12, color: themeToken.colorTextQuaternary }}>
              v1.0 &middot; &copy; 2026 OKTech Solutions
            </Text>
          </div>
        </div>
      </Content>
    </Layout>
  );
};
